// src/services/userService.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class UserService {
  // Get user profile by ID
  static async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'pb-users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  // Update user profile
  static async updateUserProfile(userId, updates) {
    try {
      const userRef = doc(db, 'pb-users', userId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updateData);
      return await this.getUserProfile(userId);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Increment user statistics
  static async incrementUserStats(userId, stats) {
    try {
      const userRef = doc(db, 'pb-users', userId);
      const updates = {};
      
      Object.entries(stats).forEach(([key, value]) => {
        updates[key] = increment(value);
      });
      
      updates.lastActiveAt = serverTimestamp();
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error updating user stats:', error);
      throw error;
    }
  }

  // Search users by email (for sharing)
  static async searchUsersByEmail(email) {
    try {
      const usersRef = collection(db, 'pb-users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  // Get users by organization
  static async getUsersByOrganization(organizationId) {
    try {
      const usersRef = collection(db, 'pb-users');
      const q = query(usersRef, where('organizationId', '==', organizationId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting organization users:', error);
      throw error;
    }
  }

  // Migrate existing user (utility function)
  static async migrateUser(userId, userData) {
    try {
      const userRef = doc(db, 'pb-users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        const profileData = {
          uid: userId,
          email: userData.email,
          displayName: userData.displayName || userData.firstName + ' ' + userData.lastName || 'User',
          photoURL: userData.photoURL || userData.profileImageUrl || null,
          
          createdAt: userData.createdAt ? new Date(userData.createdAt) : serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastSignInAt: serverTimestamp(),
          lastActiveAt: serverTimestamp(),
          
          role: 'user',
          plan: 'free',
          status: 'active',
          
          booksCreated: 0,
          totalViews: 0,
          totalInteractions: 0,
          
          migrated: true,
          migratedAt: serverTimestamp()
        };
        
        await setDoc(userRef, profileData);
        console.log(`Migrated user: ${userId}`);
        return profileData;
      }
      
      return userSnap.data();
    } catch (error) {
      console.error('Error migrating user:', error);
      throw error;
    }
  }

  async saveUserApiKeys(userId, apiKeys) {
    try {
      const userRef = db.collection('pb-users').doc(userId);
      
      // Encrypt sensitive data (basic obfuscation - use proper encryption in production)
      const encryptedKeys = {};
      if (apiKeys.gemini) {
        encryptedKeys.gemini = btoa(apiKeys.gemini); // Base64 encode
      }
      
      await userRef.set({
        apiKeys: encryptedKeys,
        updatedAt: serverTimestamp()
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.error('Error saving API keys:', error);
      throw error;
    }
  }

  async getUserApiKeys(userId) {
    try {
      const userDoc = await db.collection('pb-users').doc(userId).get();
      
      if (!userDoc.exists) {
        return null;
      }

      const data = userDoc.data();
      
      // Decrypt API keys
      const apiKeys = {};
      if (data.apiKeys?.gemini) {
        apiKeys.gemini = atob(data.apiKeys.gemini); // Base64 decode
      }
      
      return apiKeys;
    } catch (error) {
      console.error('Error getting API keys:', error);
      return null;
    }
  }
}